var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/assets/icons/LibrarySvg';
import ConfirmModal from '@/components/UI/ConfirmModal';
import { useNotification } from '@/hooks/useNotification';
const BoardListItem = ({ board, isEditing, editTitle, onEditTitleChange, onEditStart, onEditSave, onEditCancel, onDelete, tooltip, onTooltipChange, }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showNotification, showError } = useNotification(2000);
    const editInputRef = useRef(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const handleCopyCode = (code) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield navigator.clipboard.writeText(code);
            showNotification(t('boards.notifications.boardCodeCopied'));
        }
        catch (_a) {
            showError(t('errors.copyError'));
        }
    });
    const handleCopyLink = (code) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const url = `${window.location.origin}/boards/${code}`;
            yield navigator.clipboard.writeText(url);
            showNotification(t('boards.notifications.shareLinkCopied'));
        }
        catch (_a) {
            showError(t('errors.copyError'));
        }
    });
    const handleDeleteClick = () => {
        setShowDeleteModal(true);
    };
    const handleDeleteConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsDeleting(true);
        try {
            yield onDelete(board.code);
            showNotification(t('boards.notifications.boardDeleted'));
            setShowDeleteModal(false);
        }
        catch (_a) {
            showError(t('errors.error'));
        }
        finally {
            setIsDeleting(false);
        }
    });
    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
    };
    const handleEditSave = () => {
        onEditSave(board);
    };
    useEffect(() => {
        if (!isEditing)
            return;
        const handleClickOutside = (event) => {
            if (editInputRef.current &&
                !editInputRef.current.contains(event.target) &&
                !event.target.closest('.icon-btn')) {
                onEditCancel();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isEditing, onEditCancel]);
    return (_jsxs(_Fragment, { children: [_jsx("li", { className: "user-room-item", children: isEditing ? (_jsxs(_Fragment, { children: [_jsx("input", { ref: editInputRef, type: "text", value: editTitle, onChange: (e) => onEditTitleChange(e.target.value), style: { marginRight: 8, minWidth: 120 } }), _jsx("button", { className: "icon-btn", onClick: handleEditSave, "aria-label": t('common.save'), children: _jsx(Icon, { name: "check" }) })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "copy-buttons-container", children: [_jsxs("button", { type: "button", className: "icon-btn", onClick: () => handleCopyCode(board.code), onMouseEnter: () => onTooltipChange({ boardCode: board.code, type: 'code' }), onMouseLeave: () => onTooltipChange({ boardCode: null, type: null }), "aria-label": t('boards.copyBoardCode'), children: [_jsx(Icon, { name: "copy_1" }), tooltip.boardCode === board.code && tooltip.type === 'code' && (_jsx("span", { className: "tooltip", children: t('rooms.copyRoomCode') }))] }), _jsxs("button", { type: "button", className: "icon-btn", onClick: () => handleCopyLink(board.code), onMouseEnter: () => onTooltipChange({ boardCode: board.code, type: 'link' }), onMouseLeave: () => onTooltipChange({ boardCode: null, type: null }), "aria-label": t('rooms.shareRoom'), children: [_jsx(Icon, { name: "share" }), tooltip.boardCode === board.code && tooltip.type === 'link' && (_jsx("span", { className: "tooltip", children: t('rooms.shareRoom') }))] })] }), _jsx("div", { className: "room-link", onClick: () => navigate(`/boards/${board.code}`), children: _jsxs("div", { className: "room-name-code", children: [_jsx("span", { children: board.title || t('boards.noName', 'Без названия') }), _jsxs("span", { children: ["\u00A0(", board.code, ")"] })] }) }), _jsxs("div", { className: "edit-buttons-container", children: [_jsxs("button", { type: "button", className: "icon-btn", onClick: () => onEditStart(board), onMouseEnter: () => onTooltipChange({ boardCode: board.code, type: 'edit' }), onMouseLeave: () => onTooltipChange({ boardCode: null, type: null }), "aria-label": t('common.edit'), children: [_jsx(Icon, { name: "pencil" }), tooltip.boardCode === board.code && tooltip.type === 'edit' && (_jsx("span", { className: "tooltip", children: t('common.edit') }))] }), _jsxs("button", { type: "button", className: "icon-btn", onClick: handleDeleteClick, onMouseEnter: () => onTooltipChange({ boardCode: board.code, type: 'delete' }), onMouseLeave: () => onTooltipChange({ boardCode: null, type: null }), "aria-label": t('common.delete'), children: [_jsx(Icon, { name: "cross" }), tooltip.boardCode === board.code && tooltip.type === 'delete' && (_jsx("span", { className: "tooltip", children: t('common.delete') }))] })] })] })) }), _jsx(ConfirmModal, { isOpen: showDeleteModal, onClose: handleDeleteCancel, title: t('boards.confirmDelete.title', 'Подтвердить удаление'), confirmText: t('common.delete', 'Удалить'), cancelText: t('common.cancel', 'Отмена'), onConfirm: handleDeleteConfirm, isLoading: isDeleting })] }));
};
export default BoardListItem;
